import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { CreateAccrualProfile } from 'components/dialog/accrual_profile/Create';
import { UpdateAccrualProfile } from 'components/dialog/accrual_profile/Update';
import { ReadAccrualCode } from 'components/dialog/accrual_codes/read';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: any;
    cleanSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openShow, setOpenShow] = useState(false);

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'accruals', 'accrual_profiles', ['add', 'update', 'detail']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row: any) => String(row?.id) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('accruals', 'accrual_profiles');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
        setOpenShow(false);
    };

    const options = [
        {
            name: t('accruals.accrual_codes.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_profiles', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            onlyOne: false,
        },
        {
            name: t('accruals.accrual_codes.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_profiles', 'detail', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length <= 0,
            onlyOne: false,
        },
        {
            name: t('accruals.accrual_codes.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'accrual_profiles', 'update', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1,
            onlyOne: true,
        },
        {
            name: t('accruals.accrual_codes.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            onlyOne: false,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>

            <CreateAccrualProfile
                open={modalRoute.action === 'add' || openAdd}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenAdd(true);
                    }
                }}
                rows={rows}
                reloading={reloading}
            />
            <ReadAccrualCode
                open={modalRoute.action === 'detail' || openShow}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenShow(true);
                    }
                }}
                selectedRecord={selectedForDialog[0]}
            />
            <UpdateAccrualProfile
                open={modalRoute.action === 'update' || openEdit}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenEdit(true);
                    }
                }}
                rows={rows}
                rowToEdit={selectedForDialog[0]}
                reloading={reloading}
                cleanSelected={cleanSelected}
            />
        </>
    );
};

export { SubMenu };
